/**
 * @license
 * Copyright 2025 Steven Roussey <sroussey@gmail.com>
 * SPDX-License-Identifier: Apache-2.0
 */

import { createServiceToken } from "@workglow/util";
import { DataPortSchemaObject, FromSchema, TypedArraySchemaOptions } from "@workglow/util/schema";
import { type ITabularMigration, type ITabularMigrationApplier } from "../migrations";
import { BaseTabularStorage, ClientProvidedKeysOption } from "./BaseTabularStorage";
import {
  AnyTabularStorage,
  AutoGeneratedKeys,
  CoveringIndexQueryOptions,
  DeleteSearchCriteria,
  InsertEntity,
  QueryOptions,
  SearchCriteria,
  SimplifyPrimaryKey,
  TabularSubscribeOptions,
} from "./ITabularStorage";
import { InMemoryTabularMigrationApplier } from "./InMemoryTabularMigrationApplier";
import { InMemoryTabularStorage } from "./InMemoryTabularStorage";

export const SHARED_IN_MEMORY_TABULAR_REPOSITORY = createServiceToken<AnyTabularStorage>(
  "storage.tabularRepository.sharedInMemory"
);

const SYNC_TIMEOUT = 1000;
const MAX_PENDING_MESSAGES = 1000;

type BroadcastMessage =
  | { type: "SYNC_REQUEST" }
  | { type: "SYNC_RESPONSE"; data: any[] }
  | { type: "PUT"; entity: any }
  | { type: "PUT_BULK"; entities: any[] }
  | { type: "DELETE"; key: any }
  | { type: "DELETE_ALL" }
  | { type: "DELETE_SEARCH"; criteria: DeleteSearchCriteria<any> };

/**
 * Tabular repository that mirrors an internal `InMemoryTabularStorage` across
 * browser tabs / windows via the BroadcastChannel API.
 */
export class SharedInMemoryTabularStorage<
  Schema extends DataPortSchemaObject,
  PrimaryKeyNames extends ReadonlyArray<keyof Schema["properties"]>,
  // computed types
  Entity = FromSchema<Schema, TypedArraySchemaOptions>,
  PrimaryKey = SimplifyPrimaryKey<Entity, PrimaryKeyNames>,
  Value = Omit<Entity, PrimaryKeyNames[number] & keyof Entity>,
  InsertType extends InsertEntity<Entity, AutoGeneratedKeys<Schema>> = InsertEntity<
    Entity,
    AutoGeneratedKeys<Schema>
  >,
> extends BaseTabularStorage<Schema, PrimaryKeyNames, Entity, PrimaryKey, Value, InsertType> {
  private channel: BroadcastChannel | null = null;
  private channelName: string;
  private inMemoryRepo: InMemoryTabularStorage<Schema, PrimaryKeyNames, Entity, PrimaryKey>;
  private isInitialized = false;
  private syncInProgress = false;
  private pendingMessages: BroadcastMessage[] = [];
  /**
   * Resolves when the current `syncFromOtherTabs()` settles, either because a
   * peer tab sent a `SYNC_RESPONSE` (drained, then resolved) or because the
   * sync timeout fired. `setupDatabase({ awaitTabSync: true })` awaits this so
   * callers that immediately read after setup do not race the replay.
   */
  private syncSettled: Promise<void> | null = null;
  private syncSettledResolver: (() => void) | null = null;

  constructor(
    channelName: string = "tabular_store",
    schema: Schema,
    primaryKeyNames: PrimaryKeyNames,
    indexes: readonly (keyof NoInfer<Entity> | readonly (keyof NoInfer<Entity>)[])[] = [],
    clientProvidedKeys: ClientProvidedKeysOption = "if-missing",
    tabularMigrations?: ReadonlyArray<ITabularMigration>
  ) {
    super(schema, primaryKeyNames, indexes, clientProvidedKeys, tabularMigrations, channelName);
    this.channelName = channelName;
    // Don't pass migrations to the inner repo — migrations are owned by the
    // outer Shared wrapper which delegates them through getMigrationApplier.
    this.inMemoryRepo = new InMemoryTabularStorage<Schema, PrimaryKeyNames, Entity, PrimaryKey>(
      schema,
      primaryKeyNames,
      indexes,
      clientProvidedKeys
    );

    this.setupEventForwarding();

    this.initializeBroadcastChannel();
  }

  private isBroadcastChannelAvailable(): boolean {
    return typeof BroadcastChannel !== "undefined";
  }

  private initializeBroadcastChannel(): void {
    if (!this.isBroadcastChannelAvailable()) {
      console.warn("BroadcastChannel is not available. Tab synchronization will not work.");
      return;
    }

    try {
      this.channel = new BroadcastChannel(this.channelName);
      this.channel.onmessage = (event: MessageEvent<BroadcastMessage>) => {
        this.handleBroadcastMessage(event.data);
      };

      this.syncFromOtherTabs();
    } catch (error) {
      console.error("Failed to initialize BroadcastChannel:", error);
    }
  }

  private setupEventForwarding(): void {
    this.inMemoryRepo.on("put", (entity) => {
      this.events.emit("put", entity);
    });
    this.inMemoryRepo.on("get", (key, entity) => {
      this.events.emit("get", key, entity);
    });
    this.inMemoryRepo.on("query", (key, entities) => {
      this.events.emit("query", key, entities);
    });
    this.inMemoryRepo.on("delete", (key) => {
      this.events.emit("delete", key);
    });
    this.inMemoryRepo.on("clearall", () => {
      this.events.emit("clearall");
    });
  }

  private async handleBroadcastMessage(message: BroadcastMessage): Promise<void> {
    if (this.syncInProgress && message.type !== "SYNC_RESPONSE") {
      // Queue messages during sync; we'll replay them after SYNC_RESPONSE.
      if (this.pendingMessages.length < MAX_PENDING_MESSAGES) {
        this.pendingMessages.push(message);
      }
      return;
    }

    switch (message.type) {
      case "SYNC_REQUEST":
        const all = await this.inMemoryRepo.getAll();
        if (this.channel && all) {
          this.channel.postMessage({
            type: "SYNC_RESPONSE",
            data: all,
          } as BroadcastMessage);
        }
        break;

      case "SYNC_RESPONSE":
        if (message.data && Array.isArray(message.data)) {
          await this.copyDataFromArray(message.data);
        }
        this.syncInProgress = false;
        await this.drainPendingMessages();
        this.resolveSyncSettled();
        break;

      case "PUT":
        await this.inMemoryRepo.put(message.entity);
        break;

      case "PUT_BULK":
        await this.inMemoryRepo.putBulk(message.entities);
        break;

      case "DELETE":
        await this.inMemoryRepo.delete(message.key);
        break;

      case "DELETE_ALL":
        await this.inMemoryRepo.deleteAll();
        break;

      case "DELETE_SEARCH":
        await this.inMemoryRepo.deleteSearch(message.criteria as DeleteSearchCriteria<Entity>);
        break;
    }
  }

  /**
   * The `while` loop covers the case where replaying a queued message triggers
   * a new sync cycle that re-queues additional messages.
   */
  private async drainPendingMessages(): Promise<void> {
    while (!this.syncInProgress && this.pendingMessages.length > 0) {
      const messages = this.pendingMessages;
      this.pendingMessages = [];
      for (const message of messages) {
        await this.handleBroadcastMessage(message);
        if (this.syncInProgress) {
          break;
        }
      }
    }
  }

  private syncFromOtherTabs(): void {
    if (!this.channel) return;

    this.syncInProgress = true;
    this.syncSettled = new Promise<void>((resolve) => {
      this.syncSettledResolver = resolve;
    });
    this.channel.postMessage({ type: "SYNC_REQUEST" } as BroadcastMessage);

    setTimeout(() => {
      if (this.syncInProgress) {
        this.syncInProgress = false;
        void this.drainPendingMessages()
          .catch((error) => {
            console.error("Failed to drain pending messages after sync timeout", error);
          })
          .finally(() => {
            this.resolveSyncSettled();
          });
      }
    }, SYNC_TIMEOUT);
  }

  private resolveSyncSettled(): void {
    const resolver = this.syncSettledResolver;
    if (resolver) {
      this.syncSettledResolver = null;
      resolver();
    }
  }

  private async copyDataFromArray(entities: Entity[]): Promise<void> {
    if (entities.length === 0) return;
    await this.inMemoryRepo.deleteAll();
    await this.inMemoryRepo.putBulk(entities);
  }

  private broadcast(message: BroadcastMessage): void {
    if (this.channel) {
      this.channel.postMessage(message);
    }
  }

  /**
   * Initialize the cross-tab sync barrier and apply any pending migrations.
   *
   * Defaults to `awaitTabSync: true` so that callers that immediately read or
   * write after `setupDatabase()` resolves see other tabs' replayed rows; the
   * fire-and-forget shape regressed in 64591e3 and caused first-call races.
   * Pass `awaitTabSync: false` only for latency-sensitive startup paths that
   * explicitly tolerate a stale initial view.
   */
  public override async setupDatabase(options: { awaitTabSync?: boolean } = {}): Promise<void> {
    if (this.isInitialized) return;
    this.isInitialized = true;
    const awaitTabSync = options.awaitTabSync ?? true;
    this.syncFromOtherTabs();
    if (awaitTabSync && this.syncSettled) {
      await this.syncSettled;
    }
    if (this.tabularMigrations && this.tabularMigrations.length > 0) {
      await this.applyTabularMigrations();
    }
  }

  /** Shared in-memory rows do not survive a process restart. */
  public isDurable(): boolean {
    return false;
  }

  public override getMigrationApplier(): ITabularMigrationApplier | null {
    return new InMemoryTabularMigrationApplier(
      this as unknown as AnyTabularStorage,
      this.channelName
    );
  }

  public async put(value: InsertType): Promise<Entity> {
    const result = await this.inMemoryRepo.put(value);
    this.broadcast({ type: "PUT", entity: result });
    return result;
  }

  public async putBulk(values: InsertType[]): Promise<Entity[]> {
    const result = await this.inMemoryRepo.putBulk(values);
    this.broadcast({ type: "PUT_BULK", entities: result });
    return result;
  }

  async get(key: PrimaryKey): Promise<Entity | undefined> {
    return await this.inMemoryRepo.get(key);
  }

  async delete(value: PrimaryKey | Entity): Promise<void> {
    await this.inMemoryRepo.delete(value);
    const { key } = this.separateKeyValueFromCombined(value as Entity);
    this.broadcast({ type: "DELETE", key });
  }

  async deleteAll(): Promise<void> {
    await this.inMemoryRepo.deleteAll();
    this.broadcast({ type: "DELETE_ALL" });
  }

  async getAll(options?: QueryOptions<Entity>): Promise<Entity[] | undefined> {
    return await this.inMemoryRepo.getAll(options);
  }

  async size(): Promise<number> {
    return await this.inMemoryRepo.size();
  }

  async getOffsetPage(offset: number, limit: number): Promise<Entity[] | undefined> {
    return await this.inMemoryRepo.getOffsetPage(offset, limit);
  }

  async query(
    criteria: SearchCriteria<Entity>,
    options?: QueryOptions<Entity>
  ): Promise<Entity[] | undefined> {
    return await this.inMemoryRepo.query(criteria, options);
  }

  override async queryIndex<K extends keyof Entity & string>(
    criteria: SearchCriteria<Entity>,
    options: CoveringIndexQueryOptions<Entity, K>
  ): Promise<Pick<Entity, K>[]> {
    return await this.inMemoryRepo.queryIndex(criteria, options);
  }

  async deleteSearch(criteria: DeleteSearchCriteria<Entity>): Promise<void> {
    await this.inMemoryRepo.deleteSearch(criteria);
    this.broadcast({
      type: "DELETE_SEARCH",
      criteria,
    });
  }

  /**
   * Delegates to the internal InMemoryTabularStorage; cross-tab changes already
   * land in that store via the BroadcastChannel handler.
   */
  public override subscribeToChanges(
    callback: (change: any) => void,
    options?: TabularSubscribeOptions
  ): () => void {
    return this.inMemoryRepo.subscribeToChanges(callback, options);
  }

  public override destroy(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.inMemoryRepo.destroy();
  }
}
